import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Plus, Trash2, Shield, UserCog, Loader2, Camera, X, CheckCircle, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";

type AppRole = "admin" | "content_manager" | "user";

interface UserWithRole {
  user_id: string;
  email: string | null;
  display_name: string | null;
  avatar_url: string | null;
  role: AppRole;
  role_id: string;
  profile_id: string | null;
  created_at: string;
  email_confirmed: boolean;
}

const roleLabels: Record<AppRole, string> = {
  admin: "Administrator",
  content_manager: "Content Manager",
  user: "User",
};

const roleColors: Record<AppRole, string> = {
  admin: "bg-primary text-primary-foreground",
  content_manager: "bg-secondary text-secondary-foreground",
  user: "bg-muted text-muted-foreground",
};

const UsersAdmin = () => {
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserWithRole | null>(null);
  const [deleteUserId, setDeleteUserId] = useState<string | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editFileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    email: "",
    display_name: "",
    role: "content_manager" as AppRole,
  });

  // Fetch users with roles and confirmation status
  const { data: users, isLoading } = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => {
      // Get all user roles
      const { data: roles, error: rolesError } = await supabase
        .from("user_roles")
        .select("*")
        .in("role", ["admin", "content_manager"]);

      if (rolesError) throw rolesError;

      // Get admin profiles for display names and avatars
      const { data: profiles, error: profilesError } = await supabase
        .from("admin_profiles")
        .select("*");

      if (profilesError) throw profilesError;

      // Get email confirmation status from auth via edge function
      let confirmationStatuses: Record<string, boolean> = {};
      try {
        const { data: statusData } = await supabase.functions.invoke("get-users-status", {
          body: { userIds: roles.map(r => r.user_id) },
        });
        if (statusData?.statuses) {
          confirmationStatuses = statusData.statuses;
        }
      } catch (err) {
        console.error("Failed to fetch confirmation statuses:", err);
      }

      // Combine the data
      const usersWithRoles: UserWithRole[] = roles.map((role) => {
        const profile = profiles.find((p) => p.user_id === role.user_id);
        return {
          user_id: role.user_id,
          email: profile?.email || null,
          display_name: profile?.display_name || null,
          avatar_url: profile?.avatar_url || null,
          role: role.role as AppRole,
          role_id: role.id,
          profile_id: profile?.id || null,
          created_at: role.created_at,
          email_confirmed: confirmationStatuses[role.user_id] ?? true,
        };
      });

      return usersWithRoles;
    },
  });

  // Upload avatar mutation
  const uploadAvatarMutation = useMutation({
    mutationFn: async ({ userId, file }: { userId: string; file: File }) => {
      const fileExt = file.name.split(".").pop();
      const fileName = `${userId}/avatar.${fileExt}`;
      
      // Upload file to storage
      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from("avatars")
        .getPublicUrl(fileName);

      // Update profile with avatar URL
      const { error: updateError } = await supabase
        .from("admin_profiles")
        .update({ avatar_url: publicUrl })
        .eq("user_id", userId);

      if (updateError) throw updateError;

      return publicUrl;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      setUploadingAvatar(null);
      toast({
        title: "Avatar updated",
        description: "Profile picture has been updated successfully.",
      });
    },
    onError: (error: Error) => {
      setUploadingAvatar(null);
      toast({
        title: "Error uploading avatar",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Remove avatar mutation
  const removeAvatarMutation = useMutation({
    mutationFn: async (userId: string) => {
      // List files in user's folder
      const { data: files } = await supabase.storage
        .from("avatars")
        .list(userId);

      if (files && files.length > 0) {
        const filePaths = files.map(f => `${userId}/${f.name}`);
        await supabase.storage.from("avatars").remove(filePaths);
      }

      // Update profile to remove avatar URL
      const { error: updateError } = await supabase
        .from("admin_profiles")
        .update({ avatar_url: null })
        .eq("user_id", userId);

      if (updateError) throw updateError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      setEditingUser(null);
      toast({
        title: "Avatar removed",
        description: "Profile picture has been removed.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error removing avatar",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Create new user mutation
  const createUserMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      // Generate a random password just to create the account - never shared
      const randomPassword = crypto.randomUUID() + "Aa1!";

      // Sign up the user with the random password
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: data.email,
        password: randomPassword,
        options: {
          emailRedirectTo: `${window.location.origin}/admin`,
          data: {
            display_name: data.display_name,
          },
        },
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error("Failed to create user");

      // Assign role
      const { error: roleError } = await supabase.from("user_roles").insert({
        user_id: authData.user.id,
        role: data.role,
      });

      if (roleError) throw roleError;

      // Send welcome email (no password included)
      try {
        const { error: emailError } = await supabase.functions.invoke("send-welcome-email", {
          body: {
            email: data.email,
            displayName: data.display_name,
            loginUrl: "https://firststage.airow.studio/admin",
          },
        });

        if (emailError) {
          console.error("Failed to send welcome email:", emailError);
        }
      } catch (emailErr) {
        console.error("Error sending welcome email:", emailErr);
      }

      // Send password reset email so the user can set their own password
      try {
        await supabase.auth.resetPasswordForEmail(data.email, {
          redirectTo: `${window.location.origin}/admin/login`,
        });
      } catch (resetErr) {
        console.error("Error sending password reset email:", resetErr);
      }

      return authData.user;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      setIsAddDialogOpen(false);
      setFormData({
        email: "",
        display_name: "",
        role: "content_manager",
      });
      toast({
        title: "User created",
        description: "The new user has been created. They will receive an email to set their password.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error creating user",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Update role mutation
  const updateRoleMutation = useMutation({
    mutationFn: async ({
      roleId,
      newRole,
    }: {
      roleId: string;
      newRole: AppRole;
    }) => {
      const { error } = await supabase
        .from("user_roles")
        .update({ role: newRole })
        .eq("id", roleId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      toast({
        title: "Role updated",
        description: "User role has been updated successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating role",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Confirm user email mutation
  const confirmUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      const { data, error } = await supabase.functions.invoke("confirm-user", {
        body: { userId },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      toast({
        title: "User confirmed",
        description: "User email has been confirmed successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error confirming user",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete user role mutation
  const deleteRoleMutation = useMutation({
    mutationFn: async (roleId: string) => {
      const { error } = await supabase
        .from("user_roles")
        .delete()
        .eq("id", roleId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      setDeleteUserId(null);
      toast({
        title: "Access revoked",
        description: "User's admin access has been revoked.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error revoking access",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email) {
      toast({
        title: "Missing fields",
        description: "Please fill in the email address.",
        variant: "destructive",
      });
      return;
    }
    createUserMutation.mutate(formData);
  };

  const handleAvatarUpload = (userId: string, file: File) => {
    if (!file.type.startsWith("image/")) {
      toast({
        title: "Invalid file type",
        description: "Please select an image file.",
        variant: "destructive",
      });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please select an image under 5MB.",
        variant: "destructive",
      });
      return;
    }
    setUploadingAvatar(userId);
    uploadAvatarMutation.mutate({ userId, file });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>, userId: string) => {
    const file = e.target.files?.[0];
    if (file) {
      handleAvatarUpload(userId, file);
    }
    e.target.value = "";
  };

  const userToDelete = users?.find((u) => u.role_id === deleteUserId);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold">User Management</h1>
          <p className="text-muted-foreground">
            Manage administrator and content manager accounts
          </p>
        </div>
        <Button onClick={() => setIsAddDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add User
        </Button>
      </div>

      {/* Role Legend */}
      <div className="flex gap-4 text-sm">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-primary" />
          <span>Administrator: Full access including user management</span>
        </div>
        <div className="flex items-center gap-2">
          <UserCog className="h-4 w-4 text-secondary-foreground" />
          <span>Content Manager: Content access, no user management</span>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Added</TableHead>
                <TableHead className="w-[120px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users?.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center py-8 text-muted-foreground"
                  >
                    No users found
                  </TableCell>
                </TableRow>
              )}
              {users?.map((user) => (
                <TableRow key={user.role_id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div 
                        className="relative group cursor-pointer"
                        onClick={() => setEditingUser(user)}
                      >
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={user.avatar_url || undefined} alt={user.display_name || ""} />
                          <AvatarFallback className="bg-muted">
                            {user.display_name?.charAt(0).toUpperCase() || "?"}
                          </AvatarFallback>
                        </Avatar>
                        <div className="absolute inset-0 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <Camera className="h-4 w-4 text-white" />
                        </div>
                        {uploadingAvatar === user.user_id && (
                          <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                            <Loader2 className="h-4 w-4 text-white animate-spin" />
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="font-medium">
                          {user.display_name || "No name"}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {user.email || "No email"}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {user.email_confirmed ? (
                      <Badge variant="outline" className="text-green-600 border-green-600">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Confirmed
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-amber-600 border-amber-600">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        Pending
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {user.email_confirmed ? (
                      <Badge variant="outline" className="text-green-600 border-green-600">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Confirmed
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-amber-600 border-amber-600">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        Pending
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Select
                      value={user.role}
                      onValueChange={(value: AppRole) =>
                        updateRoleMutation.mutate({
                          roleId: user.role_id,
                          newRole: value,
                        })
                      }
                      disabled={user.user_id === currentUser?.id}
                    >
                      <SelectTrigger className="w-[180px]">
                        <SelectValue>
                          <Badge
                            variant="secondary"
                            className={roleColors[user.role]}
                          >
                            {roleLabels[user.role]}
                          </Badge>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">
                          <div className="flex items-center gap-2">
                            <Shield className="h-4 w-4" />
                            Administrator
                          </div>
                        </SelectItem>
                        <SelectItem value="content_manager">
                          <div className="flex items-center gap-2">
                            <UserCog className="h-4 w-4" />
                            Content Manager
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    {new Date(user.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {!user.email_confirmed && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => confirmUserMutation.mutate(user.user_id)}
                          disabled={confirmUserMutation.isPending}
                          className="text-green-600 hover:text-green-700"
                          title="Confirm user email"
                        >
                          {confirmUserMutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <CheckCircle className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteUserId(user.role_id)}
                        disabled={user.user_id === currentUser?.id}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const userId = fileInputRef.current?.dataset.userId;
          if (userId) handleFileSelect(e, userId);
        }}
      />

      {/* Edit Avatar Dialog */}
      <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Profile Picture</DialogTitle>
          </DialogHeader>
          {editingUser && (
            <div className="flex flex-col items-center gap-6 py-4">
              <div className="relative">
                <Avatar className="h-32 w-32">
                  <AvatarImage src={editingUser.avatar_url || undefined} alt={editingUser.display_name || ""} />
                  <AvatarFallback className="bg-muted text-4xl">
                    {editingUser.display_name?.charAt(0).toUpperCase() || "?"}
                  </AvatarFallback>
                </Avatar>
                {uploadingAvatar === editingUser.user_id && (
                  <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                    <Loader2 className="h-8 w-8 text-white animate-spin" />
                  </div>
                )}
              </div>
              <div className="text-center">
                <div className="font-medium text-lg">{editingUser.display_name || "No name"}</div>
                <div className="text-sm text-muted-foreground">{editingUser.email || "No email"}</div>
              </div>
              <div className="flex gap-3">
                <input
                  ref={editFileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFileSelect(e, editingUser.user_id)}
                />
                <Button
                  variant="outline"
                  onClick={() => editFileInputRef.current?.click()}
                  disabled={uploadingAvatar === editingUser.user_id}
                >
                  <Camera className="mr-2 h-4 w-4" />
                  {editingUser.avatar_url ? "Replace" : "Upload"} Photo
                </Button>
                {editingUser.avatar_url && (
                  <Button
                    variant="outline"
                    onClick={() => removeAvatarMutation.mutate(editingUser.user_id)}
                    disabled={removeAvatarMutation.isPending}
                    className="text-destructive hover:text-destructive"
                  >
                    {removeAvatarMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <X className="mr-2 h-4 w-4" />
                        Remove
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingUser(null)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                placeholder="user@example.com"
                required
              />
            </div>
            <p className="text-sm text-muted-foreground">
              The user will receive an email with a link to set their own password.
            </p>
            <div className="space-y-2">
              <Label htmlFor="display_name">Display Name</Label>
              <Input
                id="display_name"
                value={formData.display_name}
                onChange={(e) =>
                  setFormData({ ...formData, display_name: e.target.value })
                }
                placeholder="John Doe"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select
                value={formData.role}
                onValueChange={(value: AppRole) =>
                  setFormData({ ...formData, role: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      Administrator
                    </div>
                  </SelectItem>
                  <SelectItem value="content_manager">
                    <div className="flex items-center gap-2">
                      <UserCog className="h-4 w-4" />
                      Content Manager
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsAddDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createUserMutation.isPending}>
                {createUserMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create User"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deleteUserId}
        onOpenChange={() => setDeleteUserId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke Admin Access?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove admin access for{" "}
              <strong>{userToDelete?.display_name || userToDelete?.email || "this user"}</strong>.
              They will no longer be able to access the admin panel.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteUserId && deleteRoleMutation.mutate(deleteUserId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteRoleMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Revoke Access"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UsersAdmin;
